import { Search } from 'lucide-react';
import {
  useId,
  useRef,
  useState,
  type InputHTMLAttributes,
  type KeyboardEvent,
  type ReactNode,
  type UIEvent,
} from 'react';
import type { StatusTone } from '../../status-colors.ts';
import { Badge } from '../Badge/Badge.tsx';
import { controlClassName, describedByOf, Field } from '../Field/Field.tsx';
import { ARROW_STEPS } from '../arrow-steps.ts';
import { usePlacementUnder } from '../popup-placement.ts';
import styles from './SearchSelect.module.css';

export type SearchSelectOption = {
  readonly value: string;
  readonly label: string;
  readonly detail?: string | undefined;
  readonly aside?: string | undefined;
  readonly badge?: { readonly label: string; readonly tone: StatusTone } | undefined;
  readonly disabled?: boolean | undefined;
};

export type SearchSelectTexts = {
  readonly loading: string;
  readonly noMatch: string;
  readonly loadMore: string;
};

export type SearchSelectProps = {
  label: string;
  placeholder: string;
  texts: SearchSelectTexts;
  query: string;
  options: readonly SearchSelectOption[];
  loading: boolean;
  hasMore: boolean;
  loadingMore: boolean;
  required?: boolean | undefined;
  helperText?: string | undefined;
  hint?: string | undefined;
  errorText?: string | undefined;
  type?: 'search' | 'tel';
  inputMode?: InputHTMLAttributes<HTMLInputElement>['inputMode'];
  stayOpenOnPick?: boolean | undefined;
  footer?: ReactNode;
  onOpen?: (() => void) | undefined;
  onQueryChange: (text: string) => void;
  onSubmitQuery?: ((text: string) => void) | undefined;
  onLoadMore: () => void;
  onPick: (value: string) => void;
};

const NO_ACTIVE = -1;
const LOAD_AHEAD_PX = 48;

const nearEnd = (list: HTMLElement): boolean =>
  list.scrollTop + list.clientHeight >= list.scrollHeight - LOAD_AHEAD_PX;

export const SearchSelect = ({
  label,
  placeholder,
  texts,
  query,
  options,
  loading,
  hasMore,
  loadingMore,
  required,
  helperText,
  hint,
  errorText,
  type = 'search',
  inputMode,
  stayOpenOnPick = false,
  footer,
  onOpen,
  onQueryChange,
  onSubmitQuery,
  onLoadMore,
  onPick,
}: SearchSelectProps) => {
  const inputId = useId();
  const listId = useId();
  const anchor = useRef<HTMLInputElement>(null);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(NO_ACTIVE);
  const place = usePlacementUnder(anchor, open);
  const activeOption = options[active];

  const openList = () => {
    setOpen(true);
    onOpen?.();
  };

  const loadMoreWhenAllowed = () => {
    if (hasMore && !loadingMore && !loading) onLoadMore();
  };

  const pick = (option: SearchSelectOption) => {
    if (option.disabled === true) return;
    onPick(option.value);
    setActive(NO_ACTIVE);
    if (!stayOpenOnPick) setOpen(false);
  };

  const move = (step: number) => {
    if (!open) {
      openList();
      return;
    }
    const next = Math.min(Math.max(active + step, 0), Math.max(options.length - 1, 0));
    setActive(next);
    if (next === options.length - 1) loadMoreWhenAllowed();
  };

  const handleKeys = (event: KeyboardEvent<HTMLInputElement>) => {
    const step = ARROW_STEPS[event.key];
    if (step !== undefined) {
      event.preventDefault();
      move(step);
      return;
    }
    if (event.key === 'Enter') {
      event.preventDefault();
      if (open && activeOption !== undefined) pick(activeOption);
      else onSubmitQuery?.(query.trim());
      return;
    }
    if (event.key === 'Escape' && open) {
      event.preventDefault();
      setOpen(false);
    }
  };

  const handleScroll = (event: UIEvent<HTMLUListElement>) => {
    if (nearEnd(event.currentTarget)) loadMoreWhenAllowed();
  };

  const statusText = loading ? texts.loading : options.length === 0 ? texts.noMatch : null;

  return (
    <div className={styles['root']}>
      <Field id={inputId} label={label} required={required} helperText={helperText} errorText={errorText}>
        <span className={styles['wrapper']}>
          <Search size={16} aria-hidden="true" className={styles['icon']} />
          <input
            ref={anchor}
            id={inputId}
            role="combobox"
            type={type}
            inputMode={inputMode}
            dir={type === 'tel' ? 'ltr' : undefined}
            className={`${controlClassName(errorText, undefined)} ${styles['input'] ?? ''}`}
            autoComplete="off"
            required={required}
            aria-invalid={errorText ? true : undefined}
            aria-describedby={describedByOf(inputId, { helperText, errorText })}
            aria-expanded={open}
            aria-controls={listId}
            aria-autocomplete="list"
            aria-busy={loading || loadingMore}
            aria-activedescendant={
              open && activeOption !== undefined ? `${listId}-${String(active)}` : undefined
            }
            placeholder={placeholder}
            value={query}
            onFocus={openList}
            onClick={openList}
            onChange={(event) => {
              setActive(NO_ACTIVE);
              openList();
              onQueryChange(event.target.value);
            }}
            onBlur={() => {
              setOpen(false);
            }}
            onKeyDown={handleKeys}
          />
        </span>
      </Field>
      {open ? (
        <div
          className={styles['popup']}
          style={place}
          onPointerDown={(event) => {
            event.preventDefault();
          }}
        >
          {hint === undefined ? null : <p className={styles['hint']}>{hint}</p>}
          <ul
            id={listId}
            role="listbox"
            aria-label={label}
            className={styles['list']}
            onScroll={handleScroll}
          >
            {options.map((option, position) => (
              // eslint-disable-next-line jsx-a11y/click-events-have-key-events
              <li
                key={option.value}
                id={`${listId}-${String(position)}`}
                role="option"
                aria-selected={position === active}
                aria-disabled={option.disabled === true ? true : undefined}
                data-active={position === active}
                className={styles['option']}
                onClick={() => {
                  pick(option);
                }}
              >
                <span className={styles['text']}>
                  <span className={styles['label']}>{option.label}</span>
                  {option.detail === undefined ? null : (
                    <span className={styles['detail']}>{option.detail}</span>
                  )}
                </span>
                {option.aside === undefined && option.badge === undefined ? null : (
                  <span className={styles['aside']}>
                    {option.aside === undefined ? null : (
                      <span className={styles['figure']}>{option.aside}</span>
                    )}
                    {option.badge === undefined ? null : (
                      <Badge tone={option.badge.tone}>{option.badge.label}</Badge>
                    )}
                  </span>
                )}
              </li>
            ))}
            {hasMore && !loading ? (
              <li role="presentation">
                <button
                  type="button"
                  tabIndex={-1}
                  className={styles['more']}
                  disabled={loadingMore}
                  onClick={loadMoreWhenAllowed}
                >
                  {loadingMore ? texts.loading : texts.loadMore}
                </button>
              </li>
            ) : null}
          </ul>
          {statusText === null ? null : (
            <p role="status" className={styles['status']}>
              {statusText}
            </p>
          )}
          {footer === undefined ? null : <div className={styles['footer']}>{footer}</div>}
        </div>
      ) : null}
    </div>
  );
};
